// eslint-disable-next-line no-unused-vars
import React, { useState, useContext } from "react";
import { Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { AdminContext } from "../context/AdminContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const { setAToken } = useContext(AdminContext);
  const { isDarkMode } = useTheme();
  const backendUrl = import.meta.env.VITE_BACKEND_URL;
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = "Email is required";
    if (!password) newErrors.password = "Password is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length === 0) {
      try {
        const { data } = await axios.post(`${backendUrl}/api/admin/login`, {
          email,
          password,
        });

        if (data.success) {
          toast.success("Admin login successful!");
          setAToken(data.token);
          localStorage.setItem("aToken", data.token);
          localStorage.setItem("role", data.role); // Save role (super_admin or sub_admin)
          localStorage.setItem("adminAccess", JSON.stringify(data.adminAccess || []));
          localStorage.setItem("adminDepartment", data.adminDepartment || "custom");
          navigate("/admins/dashboard"); // Redirect to admin dashboard
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        if (error.response) {
          console.error("Error Response:", error.response);
          if (error.response.status === 401) {
            toast.error("Invalid credentials. Please try again.");
          } else if (error.response.status === 403) {
            toast.error("Account deactivated. Please contact the super admin.");
          } else {
            toast.error("Login failed. Please try again.");
          }
        } else {
          console.error("Unexpected error during login:", error);
          toast.error("Login failed. Please try again.");
        }
      }
    } else {
      setErrors(formErrors);
    }
  };

  return (
    <div
      className={`min-h-screen ${
        isDarkMode
          ? "bg-dark-background"
          : "bg-gradient-to-br from-[#f9b1d5] to-primary"
      } py-12 px-4 sm:px-6 lg:px-8 flex flex-col justify-center relative overflow-hidden`}
    >
      {/* Background decorative circles */}
      <div
        className={`absolute top-0 left-0 w-64 h-64 rounded-full ${
          isDarkMode ? "bg-dark-surface" : "bg-primary"
        } opacity-100 -translate-x-1/2 -translate-y-1/2`}
      ></div>
      <div
        className={`absolute bottom-0 right-0 w-96 h-96 rounded-full ${
          isDarkMode ? "bg-dark-surface" : "bg-primary"
        } opacity-100 translate-x-1/2 translate-y-1/2`}
      ></div>
      <div
        className={`absolute top-1/4 left-1/4 w-48 h-48 rounded-full ${
          isDarkMode ? "bg-dark-surface" : "bg-primary"
        } opacity-100`}
      ></div>
      <div
        className={`absolute top-1/2 right-1/4 w-48 h-48 rounded-full ${
          isDarkMode ? "bg-dark-surface" : "bg-primary"
        } opacity-100`}
      ></div>

      {/* Login Card */}
      <div
        className={`lg:w-[45%] w-[90%] mx-auto ${
          isDarkMode ? "bg-dark-surface" : "bg-card"
        } rounded-xl shadow-2xl p-8 relative z-10 backdrop-blur-sm bg-opacity-90`}
      >
        <div className="text-center mb-8">
          <h1
            className={`text-4xl font-bold ${
              isDarkMode ? "text-dark-text" : "text-text"
            } mb-2`}
          >
            Mogzit In Home Care Plc
          </h1>
          <p
            className={`${isDarkMode ? "text-dark-muted" : "text-text"} text-lg`}
          >
            Motherly Care Right Here!
          </p>
          <p
            className={`${
              isDarkMode ? "text-dark-text" : "text-text"
            } text-xl font-semibold mt-2`}
          >
            Admin Login
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Input */}
          <div>
            <label
              htmlFor="email"
              className={`block text-sm font-medium ${
                isDarkMode ? "text-dark-text" : "text-text"
              }`}
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1 block w-full px-3 py-2 border-b-2 ${
                errors.email
                  ? "border-red-500"
                  : isDarkMode
                    ? "border-dark-border"
                    : "border-gray-400"
              } focus:border-primary bg-transparent focus:outline-none transition-colors duration-200 ${
                isDarkMode ? "text-dark-text" : "text-text"
              }`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Password Input */}
          <div>
            <label
              htmlFor="password"
              className={`block text-sm font-medium ${
                isDarkMode ? "text-dark-text" : "text-text"
              }`}
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border-b-2 ${
                  errors.password
                    ? "border-red-500"
                    : isDarkMode
                      ? "border-dark-border"
                      : "border-gray-400"
                } focus:border-primary bg-transparent focus:outline-none transition-colors duration-200 ${
                  isDarkMode ? "text-dark-text" : "text-text"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                  isDarkMode
                    ? "text-dark-muted hover:text-primary"
                    : "text-text hover:text-primary"
                }`}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-500">{errors.password}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="space-y-4">
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-[#B01E66] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
            >
              Sign-In
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
